import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiChevronLeft, FiToggleLeft, FiToggleRight, FiSearch,
  FiCheck, FiX, FiShield, FiEye, FiEyeOff, FiDatabase,
} from 'react-icons/fi';
import request from '../utils/request';

const BADGE = {
  active: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  inactive: { bg: 'bg-zinc-50', text: 'text-zinc-600', border: 'border-zinc-200', dot: 'bg-zinc-400' },
  restricted: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
};

function FieldCard({ title, badge, fields, onToggle }) {
  const b = BADGE[badge] || BADGE.inactive;
  return (
    <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${b.dot}`} />
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${b.bg} ${b.text} border ${b.border}`}>
          {fields.length}
        </span>
      </div>
      {fields.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-zinc-400">No fields in this category</div>
      ) : (
        <div className="divide-y divide-zinc-50 max-h-[400px] overflow-y-auto">
          {fields.map((f) => (
            <div key={f._id || f.field_name || f.path} className="flex items-center justify-between px-5 py-3 hover:bg-zinc-50/60 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-zinc-800 truncate">{f.target_field || f.field_name || f.path}</p>
                <p className="text-xs text-zinc-400 mt-0.5 truncate">
                  {f.source_collection && `${f.source_collection}.${f.source_field}`}
                  {f.exposure_level && ` · ${f.exposure_level}`}
                  {f.target_table && ` → ${f.target_table}`}
                </p>
              </div>
              {onToggle && (
                <button
                  onClick={() => onToggle(f)}
                  className="ml-3 flex-shrink-0"
                >
                  {f.enabled ? (
                    <FiToggleRight className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <FiToggleLeft className="w-5 h-5 text-zinc-300" />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HostFeatureSettingsScreen() {
  const { hostId } = useParams();
  const navigate = useNavigate();
  const [host, setHost] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [policyRules, setPolicyRules] = useState([]);
  const [discoveredFields, setDiscoveredFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldSearch, setFieldSearch] = useState('');
  const [activeFieldTab, setActiveFieldTab] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [hostsRes, mappingsRes, policiesRes, propsFields, rawFields, hostFields] = await Promise.all([
          request.get('/studio/api/hosts'),
          request.get('/studio/api/field-mappings'),
          request.get('/studio/api/policy-rules'),
          request.get('/studio/api/fields/properties').catch(() => ({ data: [] })),
          request.get('/studio/api/fields/rawdatas').catch(() => ({ data: [] })),
          request.get('/studio/api/fields/hosts').catch(() => ({ data: [] })),
        ]);
        const found = hostsRes.data.find(h => h._id === hostId);
        setHost(found || null);
        setMappings(mappingsRes.data || []);
        setPolicyRules(policiesRes.data || []);
        setDiscoveredFields([
          ...(propsFields.data || []).map(f => ({ ...f, _collection: 'properties' })),
          ...(rawFields.data || []).map(f => ({ ...f, _collection: 'rawdatas' })),
          ...(hostFields.data || []).map(f => ({ ...f, _collection: 'hosts' })),
        ]);
      } catch (err) {
        console.error('Failed to load host detail:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hostId]);

  const activeFields = useMemo(() => mappings.filter(m => m.enabled), [mappings]);
  const inactiveFields = useMemo(() => mappings.filter(m => !m.enabled), [mappings]);
  const restrictedFields = useMemo(() => {
    const restrictedLevels = new Set(['internal_only', 'secret_restricted']);
    return policyRules.filter(r => restrictedLevels.has(r.exposure_level));
  }, [policyRules]);

  const filteredDiscovered = useMemo(() => {
    let list = discoveredFields;
    if (fieldSearch) {
      const q = fieldSearch.toLowerCase();
      list = list.filter(f => (f.path || '').toLowerCase().includes(q) || (f._collection || '').toLowerCase().includes(q));
    }
    if (activeFieldTab !== 'all') {
      list = list.filter(f => f._collection === activeFieldTab);
    }
    return list;
  }, [discoveredFields, fieldSearch, activeFieldTab]);

  const mappedPaths = useMemo(() => {
    const set = new Set();
    mappings.forEach(m => set.add(`${m.source_collection}.${m.source_field}`));
    return set;
  }, [mappings]);

  const toggleMapping = useCallback(async (field) => {
    try {
      const res = await request.patch(`/studio/api/field-mappings/${field._id}/toggle`);
      setMappings(prev => prev.map(m => m._id === field._id ? { ...m, enabled: res.data.enabled } : m));
    } catch (err) {
      console.error('Toggle mapping failed:', err);
    }
  }, []);

  const togglePolicy = useCallback(async (rule) => {
    try {
      const res = await request.patch(`/studio/api/policy-rules/${rule._id}/toggle`);
      setPolicyRules(prev => prev.map(r => r._id === rule._id ? { ...r, enabled: res.data.enabled } : r));
    } catch (err) {
      console.error('Toggle policy failed:', err);
    }
  }, []);

  const toggleHostAI = useCallback(async () => {
    if (!host) return;
    try {
      const res = await request.patch(`/studio/api/hosts/${host._id}/ai-toggle`);
      setHost(prev => ({ ...prev, aiConciergeEnabled: res.data.aiConciergeEnabled }));
    } catch (err) {
      console.error('Toggle AI failed:', err);
    }
  }, [host]);

  const toggleHostGuidebook = useCallback(async () => {
    if (!host) return;
    try {
      const res = await request.patch(`/studio/api/hosts/${host._id}/guidebook-toggle`);
      setHost(prev => ({ ...prev, guidebookEnabled: res.data.guidebookEnabled }));
    } catch (err) {
      console.error('Toggle Guidebook failed:', err);
    }
  }, [host]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-40">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!host) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-40 text-zinc-400">
        <p className="text-lg font-medium mb-2">Host not found</p>
        <button onClick={() => navigate('/ai-concierge')} className="text-sm text-indigo-600 hover:text-indigo-700">
          Back to AI Concierge
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Breadcrumb & Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/ai-concierge')}
            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors mb-4"
          >
            <FiChevronLeft className="w-4 h-4" /> AI Concierge
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center text-lg font-bold text-zinc-600">
                {(host.firstName || '?')[0]}{(host.lastName || '?')[0]}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">{host.firstName} {host.lastName}</h1>
                <p className="text-sm text-zinc-500">{host.email || 'No email'}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={toggleHostAI}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  host.aiConciergeEnabled
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'
                }`}
              >
                {host.aiConciergeEnabled ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
                {host.aiConciergeEnabled ? 'AI Active' : 'AI Disabled'}
              </button>
              <button
                onClick={toggleHostGuidebook}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  host.guidebookEnabled
                    ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                    : 'bg-zinc-50 text-zinc-600 border-zinc-200 hover:bg-zinc-100'
                }`}
              >
                {host.guidebookEnabled ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
                {host.guidebookEnabled ? 'Guidebook Active' : 'Guidebook Disabled'}
              </button>
            </div>
          </div>
        </div>

        {/* Three-column field status cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          <FieldCard
            title="Active Fields"
            badge="active"
            fields={activeFields}
            onToggle={toggleMapping}
          />
          <FieldCard
            title="Inactive Fields"
            badge="inactive"
            fields={inactiveFields}
            onToggle={toggleMapping}
          />
          <FieldCard
            title="Restricted Fields"
            badge="restricted"
            fields={restrictedFields}
            onToggle={togglePolicy}
          />
        </div>

        {/* Available Fields (Discovered) */}
        <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
                  <FiDatabase className="w-4 h-4 text-zinc-400" />
                  Available Fields
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">{discoveredFields.length} fields discovered across all collections</p>
              </div>
              <div className="flex gap-2 items-center">
                <div className="relative">
                  <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Filter fields..."
                    value={fieldSearch}
                    onChange={(e) => setFieldSearch(e.target.value)}
                    className="pl-8 pr-3 py-2 text-xs rounded-lg border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 outline-none w-48 transition-all"
                  />
                </div>
                {['all', 'properties', 'rawdatas', 'hosts'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveFieldTab(tab)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors capitalize ${
                      activeFieldTab === tab
                        ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                        : 'bg-zinc-50 text-zinc-500 border border-zinc-200 hover:bg-zinc-100'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-50/95 backdrop-blur-sm">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Field Path</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Type</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Collection</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">V1 Target</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Mapped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {filteredDiscovered.map((f, i) => {
                  const isMapped = mappedPaths.has(`${f._collection}.${f.path}`);
                  return (
                    <tr key={`${f._collection}-${f.path}-${i}`} className="hover:bg-zinc-50/60 transition-colors">
                      <td className="px-5 py-3">
                        <span className="font-mono text-xs text-zinc-700">{f.path}</span>
                      </td>
                      <td className="px-5 py-3 text-zinc-500 text-xs">{(f.types || []).join(', ') || '—'}</td>
                      <td className="px-5 py-3">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600">{f._collection}</span>
                      </td>
                      <td className="px-5 py-3 text-xs text-zinc-500">{f.suggested_v1_target || '—'}</td>
                      <td className="px-5 py-3 text-center">
                        {isMapped ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                            <FiCheck className="w-3.5 h-3.5" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-zinc-400">
                            <FiX className="w-3.5 h-3.5" /> No
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredDiscovered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-zinc-400 text-sm">
                      No fields found matching your filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/40 text-xs text-zinc-400">
            Showing {filteredDiscovered.length} of {discoveredFields.length} discovered fields
          </div>
        </div>
      </div>
    </div>
  );
}
